import { X } from '@phosphor-icons/react';
import { useRef, type ReactNode } from 'react';
import { Dialog, Heading as AriaHeading, Modal, ModalOverlay } from 'react-aria-components';
import { IconButton } from '../atoms/Button';
import { cn } from '../lib/cn';
import { useVerticalDrag } from '../lib/drag';
import { uiText } from '../i18n';

export interface SheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: ReactNode;
  /** Close by tapping outside (default). Off for flows that must not be lost by accident. */
  isDismissable?: boolean;
  /** "wide" for content that lays out in columns on big screens (e.g. weather). */
  size?: 'narrow' | 'wide';
  className?: string;
}

/** Pulled down this far (px) – or flicked down – the sheet closes. */
const CLOSE_AT = 80;
/** The top of the sheet that drags it: grip and title row (px). */
const HANDLE = 96;

/**
 * Bottom sheet on phones (thumb zone), centred dialog from tablet width up.
 * React Aria handles focus trap, Escape, scroll lock and restoring focus. On phones the whole top
 * (grip and title) pulls the sheet down: it follows the finger, stays where it is held and closes
 * when let go far enough or with a flick (Mike, 14.10.) – the close button stays for everyone.
 */
export function Sheet({
  isOpen,
  onOpenChange,
  title,
  children,
  isDismissable = true,
  size = 'narrow',
  className,
}: SheetProps) {
  const body = useRef<HTMLDivElement>(null);
  /** The sheet itself (React Aria's modal around our content). */
  const sheet = () => body.current?.parentElement ?? null;
  const drag = useVerticalDrag({
    direction: 'down',
    from: () => 0,
    canStart: (e) => {
      const el = sheet();
      if (!el || !isDismissable || window.matchMedia('(min-width: 48rem)').matches) return false;
      return e.clientY - el.getBoundingClientRect().top < HANDLE;
    },
    onMove: (o) => {
      const el = sheet();
      if (!el) return;
      el.style.transition = 'none';
      el.style.transform = `translateY(${Math.max(0, o)}px)`;
    },
    onEnd: (o, v) => {
      const el = sheet();
      if (!el) return;
      if (o > CLOSE_AT || v > 0.4) {
        onOpenChange(false);
        return;
      }
      el.style.transition = 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)';
      el.style.transform = '';
    },
  });
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      className={cn(
        'fixed inset-0 z-50 flex items-end justify-center bg-surface-scrim md:items-center md:p-gutter',
        'data-entering:animate-rise',
      )}
    >
      <Modal
        className={cn(
          'w-full rounded-t-sheet bg-surface-overlay pb-safe shadow-floating md:rounded-sheet md:pb-0',
          size === 'wide' ? 'max-w-default' : 'max-w-narrow',
          'data-entering:animate-rise',
          className,
        )}
      >
        <div ref={body} onPointerDownCapture={drag} className="contents">
          {isDismissable && (
            <div
              aria-hidden
              className="flex h-6 cursor-grab touch-none items-center justify-center md:hidden"
            >
              <span className="h-1 w-10 rounded-chip bg-border-strong" />
            </div>
          )}
          <Dialog className="flex max-h-sheet flex-col gap-gap-lg overflow-y-auto p-inset-lg outline-none">
            {({ close }) => (
              <>
                <div
                  className={cn(
                    'flex items-start justify-between gap-gap-md',
                    isDismissable && 'max-md:touch-none',
                  )}
                >
                  <AriaHeading
                    slot="title"
                    className="font-display text-xl font-black tracking-tight"
                  >
                    {title}
                  </AriaHeading>
                  <IconButton
                    icon={X}
                    aria-label={uiText().close}
                    onPress={close}
                    className="-mr-inset-xs -mt-inset-xs"
                  />
                </div>
                {children}
              </>
            )}
          </Dialog>
        </div>
      </Modal>
    </ModalOverlay>
  );
}
